import type {
  BaseConditional,
  CombinedConditional,
  ConditionalConfig,
  FieldsWithConditional,
  FormField,
  FormFields,
  FormPageType,
  FormState,
  GroupedConditional,
  OptionObject,
  PageSectionField,
  PageVisibilityContext,
} from "~/types/forms";

interface FormOptionDirectionParams {
  directionSetting: "default" | "horizontal" | "vertical";
  options: unknown[];
}

/**
 * Determines the direction in which form options should be displayed
 * based on the provided direction setting and the number of options.
 *
 * @param params - An object containing the following properties:
 *   @param params.directionSetting - A string indicating the desired direction setting.
 *     If set to "default", the function will decide the direction based on the number of options.
 *   @param params.options - An array of options to be displayed in the form.
 *
 * @returns A string representing the direction of the form options:
 *   - "horizontal" if the directionSetting is "default" and there are more than 2 options.
 *   - "vertical" if the directionSetting is "default" and there are 2 or fewer options.
 *   - The value of `directionSetting` if it is not "default".
 */
export const formOptionDirection = ({
  directionSetting,
  options,
}: FormOptionDirectionParams) => {
  if (directionSetting === "default") {
    return options.length > 2 ? "horizontal" : "vertical";
  }

  return directionSetting;
};

// Type guard to check if a field type supports conditionals
export function fieldTypeSupportsConditional(
  field: FormField
): field is FieldsWithConditional {
  // Check if the field type actually has the conditional property
  // by verifying it's not in the FieldsWithoutConditional type
  const fieldsWithoutConditional: FormField["blockType"][] = ["documentName"];
  return !fieldsWithoutConditional.includes(field.blockType);
}

// Type guard to check if conditional is a base (single) conditional
export function isBaseConditional(
  conditional: ConditionalConfig
): conditional is BaseConditional {
  return "fieldSelector" in conditional;
}

// Type guard to check if conditional is a combined conditional
export function isCombinedConditional(
  conditional: ConditionalConfig
): conditional is CombinedConditional {
  return "logic" in conditional && "conditions" in conditional;
}

// Type guard to check if conditional is a grouped conditional (ACF-style)
export function isGroupedConditional(
  conditional: ConditionalConfig
): conditional is GroupedConditional {
  return "grp" in conditional;
}

// Type guard to check if a field has a conditional
export function fieldHasConditional(
  field: FormField
): field is FormField & { cond: ConditionalConfig } {
  return (
    fieldTypeSupportsConditional(field) &&
    field.cond !== undefined &&
    field.cond !== null
  );
}

// Type guard to check if a field's conditional is enabled
export function fieldHasEnabledConditional(field: FormField): boolean {
  return fieldHasConditional(field) && field.cond.enableConditional === true;
}

// Type guard to check if a field supports defaultValue
export function fieldSupportsDefaultValue(field: FormField): boolean {
  return "defaultValue" in field ? field.defaultValue !== undefined : false;
}

export const standardiseValue = (
  value?: boolean | string | null | undefined
): boolean | string => {
  if (typeof value === "undefined" || value === null) {
    return "";
  }

  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null") return false;

  return value;
};

// Helper to parse field selectors with dot notation (e.g., "property-selector.id")
const parseFieldSelector = (
  selector: string
): { fieldName: string; propertyPath?: string } => {
  const parts = selector.split(".");
  if (parts.length === 1) {
    return { fieldName: selector };
  }
  return {
    fieldName: parts[0],
    propertyPath: parts.slice(1).join("."),
  };
};

// Helper to get nested values from objects
const getNestedValue = (obj: any, path?: string): any => {
  if (!path || !obj) return obj;

  const parts = path.split(".");
  let current = obj;

  for (const part of parts) {
    if (current && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
};

// Helper to check if a value matches an option ID
const isOptionId = (
  value: string,
  options: OptionObject[] | undefined
): boolean => {
  if (!options || !value) return false;
  return options.some((option) => option.id === value);
};

// Helper to get the ID of an option by its value
const getIdOfSelectedOption = (
  value: string,
  options: OptionObject[] | undefined
): string => {
  if (!options || !value) return value;
  const option = options.find((opt) => opt.value === value);
  return option?.id || value;
};

// Helper function to evaluate a single condition
const evaluateSingleCondition = (
  condition: {
    fieldSelector: string;
    conditionalIs: "equal" | "not-equal" | "exists" | "not-exists";
    value?: string;
  },
  formState: FormState,
  allFields: FormFields
): boolean => {
  // Parse the field selector to handle dot notation
  const { fieldName, propertyPath } = parseFieldSelector(
    condition.fieldSelector
  );

  // Find the controlling field using the base field name/slug
  const controllingField = allFields?.find((field) => field.name === fieldName);

  if (!controllingField) {
    if (import.meta.env.VITE_APP_ENVIRONMENT === "development") {
      console.warn(
        `Conditional references non-existent field "${condition.fieldSelector}"`
      );
    }
    return true; // Show by default if field not found
  }

  // Get current value from form state
  let currentValue: unknown = undefined;
  if ("name" in controllingField && typeof controllingField.name === "string") {
    const baseValue = formState[controllingField.name];
    // If there's a property path, navigate to the nested value
    currentValue = getNestedValue(baseValue, propertyPath);
  }

  // Handle exists/not-exists operators
  if (
    condition.conditionalIs === "exists" ||
    condition.conditionalIs === "not-exists"
  ) {
    let hasValue = false;

    // Check if the field has a meaningful value based on its type
    if (controllingField.blockType === "checkbox") {
      // For checkboxes, "exists" means it's checked (true)
      hasValue = currentValue === true || currentValue === "true";
    } else if (controllingField.blockType === "repeater") {
      // For repeaters, check if array has items
      hasValue = Array.isArray(currentValue) && currentValue.length > 0;
    } else if (controllingField.blockType === "checkboxGroup") {
      // For checkboxGroup, check if array has selected items (filter out false values)
      hasValue =
        Array.isArray(currentValue) && currentValue.filter(Boolean).length > 0;
    } else if (
      controllingField.blockType === "number" ||
      controllingField.blockType === "currency"
    ) {
      // For numbers, any numeric value including 0 counts as exists
      hasValue =
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== "";
    } else {
      // For all other fields (text, select, radio, etc.), check for non-empty value
      hasValue =
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== "";
    }

    return condition.conditionalIs === "exists" ? hasValue : !hasValue;
  }

  // Handle equal/not-equal operators
  const expectedValue = standardiseValue(condition.value);

  // Special handling for select and radioGroup fields
  if (
    controllingField.blockType === "select" ||
    controllingField.blockType === "radioGroup"
  ) {
    const options =
      controllingField.blockType === "select"
        ? (controllingField.selectOptions as OptionObject[])
        : (controllingField.radioOptions as OptionObject[]);

    // Check if the expected value is an option ID (form behavior)
    // or a direct value (wizard behavior)
    if (isOptionId(String(expectedValue), options)) {
      // Form behavior: compare IDs
      const currentOptionId = getIdOfSelectedOption(
        String(currentValue),
        options
      );
      const isEqual = currentOptionId === expectedValue;
      return condition.conditionalIs === "equal" ? isEqual : !isEqual;
    }
    // Wizard behavior: compare values directly (fall through to default)
  }

  // Special handling for checkboxGroup fields
  if (controllingField.blockType === "checkboxGroup") {
    const options = controllingField.checkboxOptions as OptionObject[];

    // Current value should be an array for checkboxGroup
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : [];

    // Check if expected value is an option ID
    if (isOptionId(String(expectedValue), options)) {
      // Form behavior: map current values to IDs and check
      const currentOptionIds = currentValues
        .filter(Boolean)
        .map((val) => getIdOfSelectedOption(String(val), options));

      const hasValue = currentOptionIds.includes(expectedValue as string);
      return condition.conditionalIs === "equal" ? hasValue : !hasValue;
    } else {
      // Wizard behavior: check values directly
      const hasValue = currentValues.includes(expectedValue);
      return condition.conditionalIs === "equal" ? hasValue : !hasValue;
    }
  }

  // Default comparison for all other field types
  const isEqual = currentValue === expectedValue;
  return condition.conditionalIs === "equal" ? isEqual : !isEqual;
};

// Evaluate conditionalRequired - returns true if the field should be required
export const evaluateConditionalRequired = (
  conditionalRequired: ConditionalConfig | undefined,
  formState: FormState,
  allFields: FormFields
): boolean => {
  if (!conditionalRequired) {
    return false;
  }
  return evaluateConditional(conditionalRequired, formState, allFields);
};

// Main function to evaluate any type of conditional
export const evaluateConditional = (
  conditional: ConditionalConfig,
  formState: FormState,
  allFields: FormFields
): boolean => {
  if (!conditional.enableConditional) {
    return true;
  }

  // Handle base conditional
  if (isBaseConditional(conditional)) {
    return evaluateSingleCondition(
      {
        fieldSelector: conditional.fieldSelector,
        conditionalIs: conditional.conditionalIs,
        value: conditional.value,
      },
      formState,
      allFields
    );
  }

  // Handle combined conditional
  if (isCombinedConditional(conditional)) {
    const results = conditional.conditions.map((condition) =>
      evaluateSingleCondition(condition, formState, allFields)
    );

    if (conditional.logic === "AND") {
      return results.every((result) => result === true);
    } else {
      // OR logic
      return results.some((result) => result === true);
    }
  }

  // Handle grouped conditional (ACF-style)
  // Within each group, conditions are AND'd
  // Between groups, groups are OR'd
  if (isGroupedConditional(conditional)) {
    // Empty groups mean no conditions to fail, show the field
    if (!conditional.grp || conditional.grp.length === 0) {
      return true;
    }
    return conditional.grp.some((group) =>
      group.cond.every((condition) =>
        evaluateSingleCondition(condition, formState, allFields)
      )
    );
  }

  // Should never reach here, but default to true
  return true;
};

export const isPageVisible = (
  page: FormPageType,
  context: PageVisibilityContext
): boolean => {
  const { formState, allFields } = context;
  const cond = page.cond;

  // No conditional = always visible
  if (!cond || !cond.enableConditional) {
    return true;
  }

  // Use the new evaluateConditional function
  return evaluateConditional(cond, formState, allFields);
};

const getDescription = (field) => {
  let description = "";

  if (typeof field.description === "object" && field.description !== null) {
    description = ""; // Rich text content, convert to empty string for now
  }

  if (!description && typeof field.description === "string") {
    description = field.description;
  }

  if (!description && typeof field.descriptionHtml === "string") {
    description = field.descriptionHtml;
  }

  return description;
};

// Get all pages from fields
export const getPagesFromFields = (fields: FormFields): FormPageType[] => {
  if (!fields) return [];

  // Check if any pageSections are present.
  const hasPageSections = fields.some(
    (field) => field?.blockType === "pageSection"
  );

  // If no pageSections are present, return an empty array.
  if (!hasPageSections) {
    console.log("no page sections");
    return [];
  }

  // Fields are a single array, so we need to group them into pages.
  // Pages are defined by the pageSection blockType.
  const pages = fields?.reduce<FormPageType[]>((acc, field) => {
    if (field.blockType === "pageSection") {
      // PageSectionField has these properties: label, name, description, subPage
      const pageSectionField = field as PageSectionField;
      acc.push({
        id: pageSectionField.id || "",
        label: pageSectionField.label || "",
        name: pageSectionField.name || "",
        description: getDescription(pageSectionField),
        subPage: pageSectionField.subPage || false,
        hideNavigation: pageSectionField.hideNavigation || false,
        fields: [],
        cond: pageSectionField.cond, // PRESERVE conditional logic
      });
    } else if (acc.length > 0) {
      const lastSection = acc[acc.length - 1];

      if (!lastSection.fields) {
        lastSection.fields = [];
      }

      // Now TypeScript knows fields is definitely an array
      lastSection.fields.push(field);
    }

    return acc;
  }, []);

  return pages;
};

// Enhanced page filtering with types
export const getVisiblePages = (
  allFields: FormFields,
  formState: FormState
): FormPageType[] => {
  if (!allFields) return [];

  const allPages = getPagesFromFields(allFields);

  const context: PageVisibilityContext = {
    formState,
    allFields: allFields,
  };

  return allPages.filter((page) => isPageVisible(page, context));
};

export const getCurrentPageValues = (
  fields: FormFields,
  formState: FormState
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};

  fields?.forEach((field) => {
    // Handle normal fields with a name
    if ("name" in field && typeof field.name === "string" && field.name) {
      if (field.blockType === "currency" || field.blockType === "number") {
        // For currency fields, we store the value as a number
        const parsed = parseFloat(formState[field.name] as string);
        values[field.name] = Number.isNaN(parsed) ? "" : parsed;
      } else if (field.blockType === "checkbox") {
        // For checkbox fields, we store the value as a boolean
        values[field.name] = Boolean(formState[field.name]);
      } else if (
        field.blockType === "select" ||
        field.blockType === "radioGroup"
      ) {
        // Get the options
        // If select then options are defined in the field
        let options: OptionObject[] = [];

        if (field.blockType === "select") {
          options = field.selectOptions || [];
        }

        if (field.blockType === "radioGroup") {
          options = field.radioOptions || [];
        }

        // Find the selected item
        const selectedItem = options.find(
          (option) => option.value === formState[field.name]
        );

        values[field.name] = selectedItem?.value || "";
      } else if (
        field.blockType === "phone" ||
        field.blockType === "text" ||
        field.blockType === "textarea" ||
        field.blockType === "advancedTextarea"
      ) {
        // For phone fields, we store the value as a string
        values[field.name] = formState[field.name] || "";
      } else if (field.blockType === "repeater") {
        // For repeater fields, preserve the array data
        values[field.name] = formState[field.name];
      } else {
        // Standardize the value if necessary
        values[field.name] = standardiseValue(
          formState[field.name] as string | boolean | null | undefined
        );
      }
    }

    // Handle date range fields
    if (field.blockType === "dateRange") {
      // startSlug and endSlug are used to identify date range fields
      const startSlug = field.startSlug;
      const endSlug = field.endSlug;
      if (startSlug && endSlug) {
        values[startSlug] = formState[startSlug];
        values[endSlug] = formState[endSlug];
      }
    }

    // documentName fields
    if (field.blockType === "documentName") {
      // Use the field's name as the key
      values["userDefinedDocumentName"] = formState.userDefinedDocumentName;
    }
  });
  return values;
};

/**
 * Interpolate template strings with values from repeater data
 */
export const interpolateTemplate = (
  template: string,
  value: string,
  index: number
): string => {
  return template
    .replace(/\{\{value\}\}/g, value)
    .replace(/\{\{index\}\}/g, String(index));
};
/**
 * Interpolate conditional configuration with template values
 */
const interpolateConditional = (
  conditional: ConditionalConfig | undefined,
  value: string,
  index: number
): ConditionalConfig | undefined => {
  if (!conditional) return undefined;

  if (isBaseConditional(conditional)) {
    return {
      ...conditional,
      fieldSelector: interpolateTemplate(
        conditional.fieldSelector,
        value,
        index
      ),
      value: conditional.value
        ? interpolateTemplate(conditional.value, value, index)
        : undefined,
    };
  }

  if (isCombinedConditional(conditional)) {
    return {
      ...conditional,
      conditions: conditional.conditions.map((condition) => ({
        ...condition,
        fieldSelector: interpolateTemplate(
          condition.fieldSelector,
          value,
          index
        ),
        value: condition.value
          ? interpolateTemplate(condition.value, value, index)
          : undefined,
      })),
    };
  }

  if (isGroupedConditional(conditional)) {
    return {
      ...conditional,
      grp: conditional.grp.map((group) => ({
        cond: group.cond.map((condition) => ({
          ...condition,
          fieldSelector: interpolateTemplate(
            condition.fieldSelector,
            value,
            index
          ),
          value: condition.value
            ? interpolateTemplate(condition.value, value, index)
            : undefined,
        })),
      })),
    };
  }

  return conditional;
};

/**
 * Expand dynamic page sections based on repeater data
 */
export const expandDynamicPages = (
  fields: FormFields,
  formState: FormState
): FormField[] => {
  if (!fields) return [];

  const expandedFields: FormField[] = [];

  fields.forEach((field) => {
    // If it's not a dynamic page section, just add it as-is
    if (field.blockType !== "dynamicPageSection") {
      expandedFields.push(field);
      return;
    }

    // Find the repeater field that controls this dynamic section
    const repeaterField = fields.find(
      (f) => f.name === field.repeaterSource.nameField
    );

    if (!repeaterField || repeaterField.blockType !== "repeater") {
      console.warn(
        `Dynamic page section "${field.name}" references non-existent or non-repeater field "${field.repeaterSource.nameField}"`
      );
      return;
    }

    // Get the repeater data from form state
    const repeaterData = formState[repeaterField.name] as Array<
      Record<string, any>
    >;

    if (!Array.isArray(repeaterData)) {
      return;
    }

    // Generate a page for each repeater item
    repeaterData.forEach((item, index) => {
      const value = item[field.repeaterSource.valueField] || "";

      // Create the page section
      const pageSection: FormField = {
        id: interpolateTemplate(field.template.name, value, index),
        blockType: "pageSection",
        blockName: interpolateTemplate(field.template.name, value, index),
        label: interpolateTemplate(field.template.label, value, index),
        name: interpolateTemplate(field.template.name, value, index),
        cond: field.cond,
        subPage: field.subPage,
      };

      expandedFields.push(pageSection);

      // Add all template fields with interpolated values
      field.template.fields.forEach((templateField) => {
        const expandedField = { ...templateField };

        // Interpolate IDs and labels
        if (expandedField.id) {
          expandedField.id = interpolateTemplate(
            expandedField.id,
            value,
            index
          );
        }
        if ("label" in expandedField && expandedField.label) {
          expandedField.label = interpolateTemplate(
            expandedField.label,
            value,
            index
          );
        }

        if ("name" in expandedField && expandedField.name) {
          expandedField.name = interpolateTemplate(
            expandedField.name,
            value,
            index
          );
        }

        if ("blockName" in expandedField && expandedField.blockName) {
          expandedField.blockName = interpolateTemplate(
            expandedField.blockName,
            value,
            index
          );
        }

        if (
          "description" in expandedField &&
          typeof expandedField.description === "string" &&
          expandedField.description
        ) {
          expandedField.description = interpolateTemplate(
            expandedField.description,
            value,
            index
          );
        }

        // Interpolate conditionals if present
        if ("cond" in expandedField && expandedField.cond) {
          expandedField.cond = interpolateConditional(
            expandedField.cond,
            value,
            index
          );
        }

        expandedFields.push(expandedField);
      });
    });
  });

  return expandedFields;
};
